import { Edit, Star } from '@mui/icons-material';
import { Box, Button, Chip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';

interface SkillTileWidgetProps {
    id: number;
    name: string;
    level: 1 | 2 | 3 | 4;
    category: string;
    type: 'skill' | 'competence';
    isEditing?: boolean;
    onEdit?: (id: number) => void;
    showStar?: boolean;
}

export default function SkillTileWidget({
    id,
    name,
    level,
    category,
    type,
    isEditing = false,
    onEdit,
    showStar = false,
}: SkillTileWidgetProps) {
    const theme = useTheme();

    const getLevelName = (level: 1 | 2 | 3 | 4) => {
        switch (level) {
            case 1:
                return 'Junior';
            case 2:
                return 'Mid-Level';
            case 3:
                return 'Senior';
            case 4:
                return 'Expert';
            default:
                return 'Junior';
        }
    };

    const getTypeColor = () => {
        return type === 'skill' ? theme.palette.primary.main : theme.palette.secondary.main;
    };

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 2,
                background: alpha('#fff', 0.7),
                border: `1px solid ${alpha(getTypeColor(), 0.3)}`,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 80,
                '&:hover': {
                    boxShadow: `0 4px 12px ${alpha(getTypeColor(), 0.2)}`,
                    transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {showStar && <Star sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {name}
                    </Typography>
                </Box>
                {onEdit && (
                    <Button
                        size="small"
                        onClick={() => onEdit(id)}
                        sx={{
                            minWidth: 'auto',
                            px: 1,
                            color: getTypeColor(),
                            '&:hover': {
                                backgroundColor: alpha(getTypeColor(), 0.1),
                            },
                        }}
                    >
                        <Edit sx={{ fontSize: 16 }} />
                    </Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                    label={type === 'skill' ? 'Fähigkeit' : 'Kompetenz'}
                    size="small"
                    sx={{
                        backgroundColor: getTypeColor(),
                        color: 'white',
                        fontSize: '0.65rem',
                        height: 18,
                        fontWeight: 600,
                    }}
                />
                <Chip
                    label={getLevelName(level)}
                    size="small"
                    sx={{
                        backgroundColor: `var(--color-skill-${level === 1 ? 'junior' : level === 2 ? 'intermediate' : level === 3 ? 'senior' : 'principal'})`,
                        color: level >= 4 ? '#ffffff' : '#1a1a1a',
                        fontSize: '0.7rem',
                        height: 20,
                    }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {category}
                </Typography>
            </Box>
        </Box>
    );
}
